/* eslint-disable max-len */
/* eslint-disable security/detect-object-injection */
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import moment from 'moment';
import { ShimmerTitle } from 'react-shimmer-effects';
import CardHeader from '../../../../components/CardHeader';
import UpdateButton from '../../../../components/UpdateButton';
import Toast from '../../../../components/Toast';
import TopBarWithShadow from '../../../../components/TopBar/TopBarWithShadow';
import FeeStructureDropdown from '../../../../components/FeeStructureDropdown';
import GlobalContext from '../../../../components/Context/GloablContext';
import { dataService } from '../../../../services/data.services';
import { GetBranchId } from '../../../../CommonMethods/GetBranchId';
import S3UploadHook from '../../../../CommonMethods/S3UploadHook';
import { downloadS3Object } from '../../../../CommonMethods/S3DownloadHook';
import SearchShimmer from '../../../../components/Shimmer/SearchShimmer';
import { useOnClickOutside } from '../../../../CommonMethods/OutsideClick';
import CustomOverLay from '../../../../components/CustomOverlay';
import { createNoticeTypeOption, fetchNoticeTypeOptions, getNoticeTypeLabel } from '../../Classroom/noticeTypeUtils';

const ALLOWED_ATTACHMENT_TYPES = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
const getAllowedAcademicYears = (academicYears = []) => (
    academicYears.filter((academicYear) => ['ongoing', 'upcoming'].includes(String(academicYear?.status || '').toLowerCase()))
);

export default function AddBroadcast({ edit }) {
    const { id } = useParams();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const forwardedContext = location.state?.broadcastContext || null;
    const isViewMode = searchParams.get('view') === 'true' || location.pathname.includes('/view/');
    const navigate = useNavigate();
    const { setToastmessage } = useContext(GlobalContext);

    const [toastErrorMessage, setToastErrorMessage] = useState('');
    const [pageLoading, setPageLoading] = useState(true);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [submitSelected, setSubmitSelected] = useState(false);

    const [academicYears, setAcademicYears] = useState(getAllowedAcademicYears(forwardedContext?.academicYears || []));
    const [selectedAY, setSelectedAY] = useState(forwardedContext?.academicYear || {});
    const [typeOptions, setTypeOptions] = useState(forwardedContext?.typeOptions || []);
    const [type, setType] = useState('general');
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [attachments, setAttachments] = useState([]);
    const [dateLabel, setDateLabel] = useState(moment().format('MMM DD, YYYY'));
    const [openDropdown, setOpenDropdown] = useState('');
    const [editContext, setEditContext] = useState({});
    const [loadingTypeOptions, setLoadingTypeOptions] = useState(true);
    const [showAddType, setShowAddType] = useState(false);
    const [newTypeLabel, setNewTypeLabel] = useState('');

    const typeRef = useRef(null);
    useOnClickOutside(typeRef, () => {
        setOpenDropdown((d) => d === 'type' ? '' : d);
        setShowAddType(false);
        setNewTypeLabel('');
    });
    const initialAcademicYearId = forwardedContext?.academicYear?._id || searchParams.get('academic_year_id') || '';
    const originalBroadcastContextRef = useRef({
        academicYearId: initialAcademicYearId,
        type: searchParams.get('type') || forwardedContext?.type || '',
        search: searchParams.get('search') || forwardedContext?.search || '',
        pageNumber: searchParams.get('page_number') || '1',
    });

    const isReadOnly = isViewMode || (edit && editContext.date && editContext.date < moment().format('YYYY-MM-DD'));
    const academicYearOptions = useMemo(() => academicYears.map((item) => ({ ...item })), [academicYears]);
    const typeDropdownOptions = useMemo(
        () => typeOptions.map((item) => ({ ...item, name: item.label })),
        [typeOptions]
    );

    const buildBroadcastListUrl = useCallback(({ academicYearId = '', typeValue = '', searchValue = '', pageNumber = '' } = {}) => {
        const params = new URLSearchParams();
        const resolvedAcademicYearId = academicYearId || selectedAY?._id || editContext.academic_year_id || '';
        const resolvedType = typeValue || originalBroadcastContextRef.current.type || '';
        const resolvedSearch = searchValue || originalBroadcastContextRef.current.search || '';
        const resolvedPageNumber = pageNumber || originalBroadcastContextRef.current.pageNumber || '1';

        if (resolvedAcademicYearId) params.set('academic_year_id', resolvedAcademicYearId);
        if (resolvedType) params.set('type', resolvedType);
        if (resolvedSearch) params.set('search', resolvedSearch);
        params.set('page_number', resolvedPageNumber);
        return `/noticeboard/broadcasts?${params.toString()}`;
    }, [editContext.academic_year_id, selectedAY?._id]);

    const fallbackBackLocation = buildBroadcastListUrl({
        academicYearId: originalBroadcastContextRef.current.academicYearId,
        typeValue: originalBroadcastContextRef.current.type,
        searchValue: originalBroadcastContextRef.current.search,
        pageNumber: originalBroadcastContextRef.current.pageNumber,
    });
    const backLocation = forwardedContext?.listUrl || fallbackBackLocation;

    const init = useCallback(async () => {
        try {
            const branch_id = await GetBranchId();
            let ongoingAYs = getAllowedAcademicYears(forwardedContext?.academicYears || []);
            if (!ongoingAYs.length) {
                const academicYearsRes = await dataService.GetAPI(`academic-years?filter=true&branch_id=${branch_id}&status=ongoing,upcoming`);
                ongoingAYs = getAllowedAcademicYears(
                    academicYearsRes.data?.data?.data || academicYearsRes.data?.data || []
                );
                setAcademicYears(ongoingAYs);
            }
            const fetchedTypeOptions = await fetchNoticeTypeOptions(branch_id, 'broadcasts');
            setTypeOptions(fetchedTypeOptions);

            if (!edit) {
                const preselectedAcademicYear = ongoingAYs.find((item) => item._id === initialAcademicYearId) || ongoingAYs[0] || {};
                if (preselectedAcademicYear?._id) {
                    setSelectedAY(preselectedAcademicYear);
                }
                if (forwardedContext?.type) {
                    setType(forwardedContext.type);
                } else if (fetchedTypeOptions.length > 0) {
                    setType((currentType) => currentType || fetchedTypeOptions[0].value);
                }
            }

            if (edit && id) {
                const res = await dataService.GetAPI(`notice-board/broadcasts/${id}?branch_id=${branch_id}`);
                const announcement = res.data?.data?.data || res.data?.data;
                if (announcement) {
                    setEditContext(announcement);
                    setSelectedAY({
                        _id: announcement.academic_year_id,
                        name: announcement.academic_year_name,
                    });
                    setType(announcement.type || 'general');
                    setTitle(announcement.title || '');
                    setDescription(announcement.description || '');
                    setAttachments(announcement.attachments || []);
                    setDateLabel(announcement.date ? moment(announcement.date).format('MMM DD, YYYY') : moment().format('MMM DD, YYYY'));
                    if (!originalBroadcastContextRef.current.academicYearId) {
                        originalBroadcastContextRef.current.academicYearId = announcement.academic_year_id || '';
                    }
                }
            }
        } catch {
            setToastErrorMessage('Something went wrong while loading');
        } finally {
            setLoadingTypeOptions(false);
            setPageLoading(false);
        }
    }, [edit, forwardedContext?.academicYears, forwardedContext?.type, id, initialAcademicYearId]);

    useEffect(() => { init(); }, [init]);

    useEffect(() => {
        if (edit || !academicYears.length) return;
        if (!selectedAY._id || !academicYears.some((item) => item._id === selectedAY._id)) {
            setSelectedAY(academicYears[0] || {});
        }
    }, [academicYears, edit, selectedAY._id]);

    const validate = () => {
        if (uploading) {
            setToastErrorMessage('Please wait for file uploads to finish');
            return false;
        }
        if (!edit && !selectedAY._id) {
            setToastErrorMessage('Please select Academic Year');
            return false;
        }
        if (!title.trim()) {
            setToastErrorMessage('Please enter a title');
            return false;
        }
        if (!description.trim()) {
            setToastErrorMessage('Please enter a description');
            return false;
        }
        return true;
    };

    const handleFileUpload = async (file) => {
        if (!file) return;
        if (attachments.length >= 3) { setToastErrorMessage('Maximum 3 attachments allowed'); return; }
        if (!ALLOWED_ATTACHMENT_TYPES.includes(file.type)) { setToastErrorMessage('Only JPG, JPEG, PNG, and PDF files are allowed'); return; }
        if (file.size > 5 * 1024 * 1024) { setToastErrorMessage('File size must not exceed 5MB'); return; }

        setUploading(true);
        try {
            const branch_id = await GetBranchId();
            const ayId = selectedAY._id || editContext.academic_year_id || 'ay';
            const path = `broadcasts/${branch_id}/${ayId}/${moment().format('YYYY-MM-DD')}/${file.name}`;
            const key = await S3UploadHook(file, path, () => {}, true);
            setAttachments((prev) => [...prev, { key, name: file.name }]);
        } catch {
            setToastErrorMessage('File upload failed. Please try again.');
        } finally {
            setUploading(false);
        }
    };

    const removeAttachment = (index) => {
        setAttachments((prev) => prev.filter((_, i) => i !== index));
    };

    const handleAttachmentDownload = async (attachment) => {
        if (!attachment?.key) return;
        try {
            await downloadS3Object(attachment.key);
        } catch {
            setToastErrorMessage('Unable to download attachment');
        }
    };

    const handleCreateAnnouncementType = async () => {
        const trimmedLabel = newTypeLabel.trim();
        if (!trimmedLabel) {
            setToastErrorMessage('Please enter a broadcast type');
            return;
        }
        try {
            setToastErrorMessage('');
            setLoadingTypeOptions(true);
            const branch_id = await GetBranchId();
            const { option, message } = await createNoticeTypeOption({
                branchId: branch_id,
                typeLabel: trimmedLabel,
                module: 'broadcasts',
            });
            if (!option) {
                setToastErrorMessage('Failed to create broadcast type');
                return;
            }
            setTypeOptions((prev) => {
                const next = [...prev.filter((item) => item.value !== option.value), option];
                return next.sort((a, b) => a.label.localeCompare(b.label));
            });
            setType(option.value);
            setNewTypeLabel('');
            setShowAddType(false);
            setToastmessage(message || 'Broadcast type created successfully');
        } catch (error) {
            setToastErrorMessage(error?.response?.data?.message || 'Failed to create broadcast type');
        } finally {
            setLoadingTypeOptions(false);
        }
    };

    const handleSave = async () => {
        setSubmitSelected(true);
        if (!validate()) return;

        setLoading(true);
        try {
            const branch_id = await GetBranchId();
            const body = {
                type,
                title: title.trim(),
                description: description.trim(),
                attachments,
            };

            let response;
            if (edit && id) {
                response = await dataService.PatchAPI(body, `notice-board/broadcasts/${id}`);
            } else {
                response = await dataService.PostAPI({
                    ...body,
                    branch_id,
                    academic_year_id: selectedAY._id,
                    academic_year_name: selectedAY.name,
                }, 'notice-board/broadcasts');
            }

            const responseData = response?.data?.data || response?.data;
            if (responseData?.status === 200 || responseData?.status === 201) {
                setToastmessage(responseData.message || 'Saved successfully');
                navigate(buildBroadcastListUrl({
                    academicYearId: edit ? (originalBroadcastContextRef.current.academicYearId || selectedAY?._id || editContext.academic_year_id || '') : selectedAY?._id,
                    typeValue: originalBroadcastContextRef.current.type,
                    searchValue: originalBroadcastContextRef.current.search,
                    pageNumber: edit ? originalBroadcastContextRef.current.pageNumber : '1',
                }));
            } else {
                setToastErrorMessage(responseData?.message || 'Something went wrong');
            }
        } catch (error) {
            setToastErrorMessage(error?.response?.data?.message || 'Something went wrong');
        } finally {
            setLoading(false);
        }
    };

    return (
        <CardHeader noPadding>
            {isReadOnly ? (
                <TopBarWithShadow
                    Tittle="View Broadcast"
                    isback={true}
                    backLocation={backLocation}
                />
            ) : (
                <div className="mainDataCntnrTop shadow-s13" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                    <h5 className="pageHeader addclass">{edit ? 'Edit Broadcast' : 'Add Broadcast'}</h5>
                    <div className="flex items-center">
                        <UpdateButton
                            ButtonName="Cancel"
                            loading={false}
                            handleOnCLick={() => navigate(backLocation)}
                            classKey="cancl-btn"
                        />
                        <UpdateButton
                            ButtonName={edit ? 'Update' : 'Add'}
                            loading={loading}
                            handleOnCLick={handleSave}
                            classKey="actn-btn onboardInstitution"
                        />
                    </div>
                </div>
            )}

            {toastErrorMessage !== '' && (
                <Toast message={toastErrorMessage} type="error" setToastmessage={setToastErrorMessage} />
            )}
            {pageLoading && (
                <CustomOverLay Active text="Loading..." classKey="flex flex-col justify-center items-center sidebarOpenedOverlay" />
            )}

            {!pageLoading && ( <div className="mainDataCntnrPageDiv scrollBlue_thick">
                <div className="2xl:w-[60%] w-[80%] mx-auto">
                    <div className="border border-[#D0D5DD] p-3 rounded mb-4">
                        {edit ? (
                            <div className="flex flex-wrap gap-6 items-end"  style={{'gap': '6rem'}}>
                                <div className="flex items-center gap-2">
                                    <p className="">Academic Year</p>
                                    <p className="font-semibold ">{selectedAY.name || '-'}</p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <p className="">Type</p>
                                    <p className="font-semibold ">{getNoticeTypeLabel(type, typeOptions, '-')}</p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <p className="">Date</p>
                                    <p className="font-semibold ">{dateLabel}</p>
                                </div>
                            </div>
                        ) : (
                            <div className="flex grid grid-cols-1 md:grid-cols-3 xl:grid-cols-3 gap-4 items-start">
                                <div className="w-[260px]">
                                    <p className=" mb-1">Academic Year <span className="text-[#F04438]">*</span></p>
                                    <FeeStructureDropdown
                                        dropDownName="academic_year"
                                        value={selectedAY?.name ? `${selectedAY.name} (${selectedAY.status || ''})` : ''}
                                        options={academicYearOptions}
                                        placeHolder="Select Academic Year"
                                        handleOnchangeInput={(_, option) => setSelectedAY(option)}
                                        showErrorBorder={submitSelected && !selectedAY._id}
                                    />
                                </div>
                                <div className="flex flex-col mr-2 relative w-[280px]" ref={typeRef}>
                                    <p className="mb-1">Type <span className="text-[#F04438]">*</span></p>
                                    <button
                                        type="button"
                                        className={`sec_filter_btn relative ml-0 w-full ${submitSelected && !type ? 'redBorder' : ''}`}
                                        style={{'padding':'0.275rem 0.5rem'}}
                                        onClick={() => {
                                            setOpenDropdown((d) => d === 'type' ? '' : 'type');
                                            setShowAddType(false);
                                            setNewTypeLabel('');
                                        }}
                                    >
                                        <p className="text-f7 text-[#000000] font-normal more-lines pr-2">
                                            {getNoticeTypeLabel(type, typeOptions, 'Select Type')}
                                        </p>
                                        <img loading="lazy" src="/images/dropdown-icon.png" alt="icon"
                                            className={`absolute top-[50%] -translate-y-[50%] dropdown-close right-4 ${openDropdown === 'type' ? 'transform rotate-180' : ''}`} />
                                    </button>
                                    {openDropdown === 'type' && (
                                        <ul className="ant-picker-dropdown slct_drp_dwn class-diary-dropdown w-full min-w-0 left-0 right-auto" style={{'left':'6px', top:'63px'}}>
                                            <li className="max-h-[220px] overflow-auto scroll-bar">
                                                <ul className="border-b border-[#E6E8EA]">
                                                    {showAddType ? (
                                                        <li className="px-2 py-1">
                                                            <div className="flex items-center gap-2">
                                                                <input
                                                                    type="text"
                                                                    className="w-full bg-transparent !border-0 !border-t-0 !border-l-0 !border-r-0 !border-b !border-[#106EB4] rounded-none outline-none focus:outline-none focus:ring-0 focus:shadow-none px-0 pb-1 text-f7 text-[#031624]"
                                                                    placeholder="New Type"
                                                                    value={newTypeLabel}
                                                                    maxLength={100}
                                                                    autoFocus
                                                                    onChange={(e) => setNewTypeLabel(e.target.value)}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === 'Enter') {
                                                                            e.preventDefault();
                                                                            handleCreateAnnouncementType();
                                                                        }
                                                                    }}
                                                                />
                                                                <button
                                                                    type="button"
                                                                    className="shrink-0"
                                                                    onClick={handleCreateAnnouncementType}
                                                                >
                                                                    <img src="/images/otp-tick.svg" alt="save type" className="w-4 h-4 prevent-click" />
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="shrink-0"
                                                                    onClick={() => {
                                                                        setShowAddType(false);
                                                                        setNewTypeLabel('');
                                                                    }}
                                                                >
                                                                    <img src="/images/file-remove.svg" alt="cancel type" className="w-4 h-4 prevent-click" />
                                                                </button>
                                                            </div>
                                                        </li>
                                                    ) : (
                                                        <li onClick={() => setShowAddType(true)}>
                                                            <a className="slct_drp_dwn_optn selected_Class !flex !flex-row items-center gap-2 whitespace-nowrap" href="javascript:void(0)" style={{ padding: '6.5px 12px ' }}>
                                                                <img src="/images/add-icon-blue.svg" alt="add type" className="w-4 h-4" />
                                                                <span className="text-[#106EB4]">New Type</span>
                                                            </a>
                                                        </li>
                                                    )}
                                                </ul>
                                                {loadingTypeOptions ? (
                                                    <div className="pl-2 pt-2"><SearchShimmer count={3} height={'110px'} /></div>
                                                ) : typeOptions.length > 0 ? typeOptions.map((item) => (
                                                    <ul key={item.value}>
                                                        <li onClick={() => {
                                                            setType(item.value);
                                                            setShowAddType(false);
                                                            setOpenDropdown('');
                                                        }}>
                                                            <a className="slct_drp_dwn_optn selected_Class" href="javascript:void(0)">{item.label}</a>
                                                        </li>
                                                    </ul>
                                                )) : (
                                                    <li><a className="slct_drp_dwn_optn">- No types available -</a></li>
                                                )}
                                            </li>
                                        </ul>
                                    )}
                                </div>
                                <div className='ml-2'>
                                    <p className=" mb-1">Date <span className="text-[#F04438]">*</span></p>
                                    <p className="font-semibold  flex items-center">{dateLabel}</p>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="border border-[#D0D5DD] p-3 rounded">
                        <div className="flex items-start mb-3">
                            <p className=" min-w-[130px] mt-1">Title <span className="text-[#F04438]">*</span></p>
                            {isReadOnly ? (
                                <p className="text-f7 text-[#031624] w-full">{title || '-'}</p>
                            ) : (
                                <div className="w-full">
                                    <input
                                        type="text"
                                        className={`slct-inpt-fld w-full ${submitSelected && !title.trim() ? 'redBorder' : ''}`}
                                        maxLength={100}
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        placeholder="Enter broadcast title"
                                    />
                                    <p className="text-xs text-right">{title.length}/100</p>
                                </div>
                            )}
                        </div>

                        <div className="flex items-start">
                            <p className=" min-w-[130px] mt-1">Description <span className="text-[#F04438]">*</span></p>
                            {isReadOnly ? (
                                <p className="text-f7 text-[#031624] w-full">{description || '-'}</p>
                            ) : (
                                <div className="w-full">
                                    <textarea
                                        className={`txt-fld w-full ${submitSelected && !description.trim() ? 'redBorder' : ''}`}
                                        maxLength={500}
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        placeholder="Enter broadcast description"
                                        style={{ height: '120px' }}
                                    />
                                    <p className="text-xs text-right">{description.length}/500</p>
                                </div>
                            )}
                        </div>

                        <div className="flex items-start">
                            <p className=" min-w-[130px] mt-3">Attachments</p>
                            <div className="w-full">
                                {attachments.map((att, index) => (
                                    <div key={`${att.key}-${index}`} className="flex items-center gap-2 mb-2">
                                        {isReadOnly ? (
                                            <button
                                                type="button"
                                                className="text-[#106EB4] underline text-left"
                                                onClick={() => handleAttachmentDownload(att)}
                                            >
                                                {att.name}
                                            </button>
                                        ) : (
                                            <p>{att.name}</p>
                                        )}
                                        {!isReadOnly && (
                                            <button type="button" onClick={() => removeAttachment(index)}>
                                                <img src="/images/file-remove.svg" alt="remove" className="w-4 h-4" />
                                            </button>
                                        )}
                                    </div>
                                ))}

                                {!isReadOnly && attachments.length < 3 && (
                                    <div>
                                        {uploading ? (
                                            <p className="text-xs text-[#818E98]">Uploading...</p>
                                        ) : (
                                            <label className="cursor-pointer inline-flex items-center gap-2  underline">
                                                <img src="/images/add-icon-blue.svg" alt="add" className="w-4 h-4" />
                                                Add Attachment ({attachments.length}/3)
                                                <input
                                                    type="file"
                                                    className="hidden"
                                                    accept=".jpg,.jpeg,.png,.pdf"
                                                    onChange={(e) => {
                                                        handleFileUpload(e.target.files?.[0]);
                                                        e.target.value = '';
                                                    }}
                                                />
                                            </label>
                                        )}
                                        <p className="text-xs text-[#818E98] mt-1">JPG, JPEG, PNG, or PDF; max 5MB each</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>)}
        </CardHeader>
    );
}
